import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Avatar,
  Skeleton,
} from '@mui/material';
import {
  HelpOutline as HelpIcon,
  Email as EmailIcon,
  Support as SupportIcon,
  Visibility as ViewIcon,
  ChatBubbleOutline as CommentIcon,
  TrendingUp as TrendingIcon,
  Forum as ForumIcon,
  EditOutlined as WriteIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import packageService from '../services/packageService';
import { useAuth } from '../contexts/AuthContext';
import { isPartnerClientBrand, useClientBranding } from '../contexts/ClientBrandingContext';
import UnsplashAttribution from '../components/UnsplashAttribution';
import HealthStatusBanner from '../components/HealthStatusBanner';
import AppStoreBadges from '../components/AppStoreBadges';
import { isMobileApp } from '../utils/platformDetection';
import { getHomepageSchemas } from '../utils/seoSchemas';
import { DOCUMENT, PAGE_CONTAINER_MAX_WIDTH, documentEyebrowSx, documentPageContainerSx } from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerLinkSx } from '../components/PartnerListChrome';
import {
  HOME_PACKAGE_CYCLE_MS,
  HOME_PACKAGE_SLOT_COUNT,
  getCycledPackages,
} from '../utils/homePackageCycle';
import { getPackagesListingPath } from '../utils/partnerCatalogStorage';

const HOME = {
  ...DOCUMENT,
  brass: '#C9A961',
  oxbloodDeep: '#2A070C',
  display: DOCUMENT.display,
};

const WRAP_SX = {
  ...documentPageContainerSx,
  maxWidth: (theme) => theme.breakpoints.values[PAGE_CONTAINER_MAX_WIDTH],
  mx: 'auto',
  width: '100%',
  boxSizing: 'border-box',
};

const SECTION_SX = {
  py: { xs: 5, sm: 7, md: 10 },
};

const fullWidthOnMobileSx = {
  width: { xs: '100%', sm: 'auto' },
  textAlign: 'center',
};

const HERO_IMAGE = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1800&q=80&auto=format';
const DOOR_TRAVEL_IMAGE = 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=900&h=600&fit=crop&q=80';
const DOOR_PARTNER_IMAGE = 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=900&h=600&fit=crop&q=80';
const BAND_IMAGE = 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1800&q=80&auto=format';
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop&q=80';

const brassBtnSx = {
  background: HOME.brass,
  color: HOME.oxbloodDeep,
  border: 0,
  borderRadius: 0,
  fontSize: 11,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  px: 3,
  py: 1.75,
  minWidth: 0,
  boxShadow: 'none',
  '&:hover': {
    background: '#DCC183',
    boxShadow: 'none',
  },
};

const ghostBtnSx = {
  background: 'rgba(255,255,255,0.07)',
  color: '#F7F1E8',
  border: '1px solid rgba(201,169,97,0.5)',
  borderRadius: 0,
  fontSize: 11,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  px: 3,
  py: 1.75,
  minWidth: 0,
  boxShadow: 'none',
  backdropFilter: 'blur(3px)',
  '&:hover': {
    background: 'rgba(201,169,97,0.16)',
    boxShadow: 'none',
  },
};

const footerLinkSx = {
  display: 'block',
  fontSize: 13.5,
  color: '#DCC9BC',
  textDecoration: 'none',
  py: 0.6,
  '&:hover': { color: '#F7F1E8' },
};

const scrollPageToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
};

const FooterNavLink = ({ to, children, sx = footerLinkSx }) => (
  <Box component={RouterLink} to={to} onClick={scrollPageToTop} sx={sx}>
    {children}
  </Box>
);

const formatMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return `$${amount.toLocaleString('en-US')}`;
};

const timeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const CoverBand = ({ src, alt, children, alignItems = 'flex-end', veilSx, photographerName, photographerUsername, minHeight, aspectRatio }) => (
  <Box
    sx={{
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems,
      minHeight: minHeight || { xs: 'auto', md: 520 },
      aspectRatio: aspectRatio === false ? 'auto' : (aspectRatio || { xs: 'auto', md: '21 / 9' }),
      bgcolor: HOME.oxbloodDeep,
    }}
  >
    <Box
      component="img"
      src={src || FALLBACK_IMAGE}
      alt={alt}
      loading="lazy"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to top, rgba(18,5,8,0.82) 0%, rgba(18,5,8,0.28) 44%, rgba(18,5,8,0.06) 72%)',
        ...veilSx,
      }}
    />
    <UnsplashAttribution
      imageUrl={src}
      photographerName={photographerName}
      photographerUsername={photographerUsername}
      variant="overlay"
    />
    <Box sx={{ position: 'relative', zIndex: 3, width: '100%', minWidth: 0 }}>{children}</Box>
  </Box>
);

const PlateImage = ({ src, alt, ratio, photographerName, photographerUsername, children, minHeight }) => (
  <Box
    component="figure"
    sx={{
      position: 'relative',
      overflow: 'hidden',
      m: 0,
      bgcolor: HOME.oxbloodDeep,
      aspectRatio: ratio,
      minHeight: minHeight || 0,
      width: '100%',
      maxWidth: '100%',
    }}
  >
    <Box
      component="img"
      src={src || FALLBACK_IMAGE}
      alt={alt}
      loading="lazy"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
    {children}
    <UnsplashAttribution
      imageUrl={src}
      photographerName={photographerName}
      photographerUsername={photographerUsername}
      variant="overlay"
    />
  </Box>
);

const Home = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { branding } = useClientBranding();
  const navigate = useNavigate();
  const partnerLogoUrl = isPartnerClientBrand(branding) ? branding.logoUrl : null;
  const homeLogoSrc = partnerLogoUrl || '/noBgWhite.png';
  const homeLogoAlt = partnerLogoUrl ? branding.name : 'Déjà Vu Nomad';

  const isPartnerLoggedIn = !!localStorage.getItem('partnerToken');
  const shouldShowAppDownload = !isMobileApp();

  const [featuredDestinations, setFeaturedDestinations] = useState([]);
  const [communityGuides, setCommunityGuides] = useState([]);
  const [guideTotal, setGuideTotal] = useState(0);
  const [destinationTotal, setDestinationTotal] = useState(0);
  const [partnerPackages, setPartnerPackages] = useState([]);
  const [generatedPackages, setGeneratedPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packageCycleOffset, setPackageCycleOffset] = useState(0);

  const [trendingPosts, setTrendingPosts] = useState([]);
  const [forumStats, setForumStats] = useState({ totalPosts: 0, totalDiscussions: 0 });
  const [forumLoading, setForumLoading] = useState(true);

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      setNewsletterError(t('home.community.invalidEmail'));
      return;
    }

    setNewsletterLoading(true);
    setNewsletterError('');

    try {
      await api.post('/newsletter/subscribe', { email: newsletterEmail });
      setNewsletterSuccess(true);
      setNewsletterEmail('');
    } catch (err) {
      if (err.response?.status === 409) {
        setNewsletterError(t('home.community.alreadySubscribed'));
      } else {
        setNewsletterError(t('home.community.subscribeFailed'));
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const [featuredRes, destRes, guidesRes] = await Promise.all([
          api.get('/destinations/featured?limit=6'),
          api.get('/destinations?limit=1'),
          api.get('/guides?sort=popular&limit=3&page=1'),
        ]);
        setFeaturedDestinations(Array.isArray(featuredRes.data) ? featuredRes.data : []);
        setDestinationTotal(destRes.data?.total || 0);
        if (guidesRes.data?.success) {
          setCommunityGuides(guidesRes.data.guides || []);
          setGuideTotal(guidesRes.data.pagination?.total || 0);
        }
      } catch (error) {
        console.error('Failed to fetch homepage catalog:', error);
      }
    };

    loadCatalog();
  }, []);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setPackagesLoading(true);
        let featured = [];
        let popular = [];
        try {
          const featuredPkgRes = await api.get('/travel-packages/featured?limit=24');
          featured = featuredPkgRes?.data?.packages || [];
        } catch (error) {
          featured = [];
        }
        try {
          const popularResult = await packageService.getPopularPackages('LAX');
          popular = Array.isArray(popularResult) ? popularResult.slice(0, 4) : [];
        } catch (error) {
          popular = [];
        }
        setPartnerPackages(featured.length > 0 ? featured : []);
        setGeneratedPackages(popular);
      } catch (error) {
        console.error('Failed to fetch vacation packages:', error);
      } finally {
        setPackagesLoading(false);
      }
    };

    fetchPackages();
  }, []);

  useEffect(() => {
    if (partnerPackages.length <= HOME_PACKAGE_SLOT_COUNT) return undefined;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setPackageCycleOffset((current) => current + 1);
    }, HOME_PACKAGE_CYCLE_MS);

    return () => window.clearInterval(timer);
  }, [partnerPackages.length]);

  useEffect(() => {
    const fetchTrendingPosts = async () => {
      try {
        setForumLoading(true);
        const response = await api.get('/forum/posts', {
          params: { sort: 'discussed', limit: 5 },
        });
        const posts = response.data?.posts || [];
        setTrendingPosts(posts);
        setForumStats({
          totalPosts: response.data?.pagination?.total || 0,
          totalDiscussions: posts.reduce((sum, p) => sum + (p.comment_count || 0), 0),
        });
      } catch (error) {
        console.error('Failed to fetch trending forum posts:', error);
        setTrendingPosts([]);
      } finally {
        setForumLoading(false);
      }
    };

    fetchTrendingPosts();
  }, []);

  const homepageSchemas = getHomepageSchemas();
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is the best app for vacation planning?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Déjà Vu Nomad is a free vacation planning app that helps you organize trips with friends and family. It includes collaborative itinerary building, real-time chat, flight and hotel booking, expense tracking, and AI-powered travel suggestions.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I plan a group vacation?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'With Déjà Vu Nomad, planning a group vacation is easy: 1) Create a trip and invite your travel companions, 2) Collaboratively build your itinerary with suggested activities, 3) Use the built-in chat to discuss plans, 4) Track shared expenses, and 5) Book flights and hotels together.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is Déjà Vu Nomad free to use?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Free to plan. Your group always joins free; the trip creator unlocks Trip Pass. Travel professionals start free. Advisor Pro is $29/mo, Group Pro is $79/mo, Agency is $149/mo. 0% commission on clients they bring. Marketplace introductions are 5–10% when we bring a new customer.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I book flights and hotels through the app?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, Déjà Vu Nomad lets you search and compare flights and hotels directly in the app. You can also browse curated vacation packages from travel agents, or build your own custom trip with our package builder.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I create a travel itinerary?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Creating a travel itinerary is simple: 1) Start a new trip with your destination and dates, 2) Add activities, restaurants, hotels, and transportation, 3) Organize items by day with drag-and-drop, 4) Add notes and details for each activity, 5) Share with your travel group for real-time collaboration.',
        },
      },
    ],
  };

  const primaryCta = () => {
    if (isPartnerLoggedIn) {
      return { label: t('home.hero.viewDashboard'), path: '/partner/dashboard' };
    }
    if (user) {
      return { label: t('home.viewTrips'), path: '/trips' };
    }
    return { label: t('home.hero.startPlanning'), path: '/register' };
  };

  const cta = primaryCta();
  const displayPackages = partnerPackages.length > 0
    ? getCycledPackages(partnerPackages, packageCycleOffset)
    : generatedPackages;
  const usingPartnerPackages = partnerPackages.length > 0;

  const mosaicRatios = ['3 / 2', '4 / 5', '4 / 5', '1 / 1', '1 / 1', '3 / 2'];
  const mosaicSpans = [2, 1, 1, 1, 1, 2];

  const trustItems = [
    destinationTotal > 0 && { label: t('home.trust.destinations'), value: destinationTotal.toLocaleString() },
    guideTotal > 0 && { label: t('home.trust.guides'), value: `${guideTotal.toLocaleString()}+` },
    forumStats.totalPosts > 0 && { label: t('home.trust.discussions'), value: forumStats.totalPosts.toLocaleString() },
    displayPackages.length > 0 && { label: t('home.trust.packages'), value: usingPartnerPackages ? displayPackages.length.toLocaleString() : generatedPackages.length.toLocaleString() },
  ].filter(Boolean);

  const openGeneratedPackage = (pkg) => {
    navigate(
      `/package/${pkg.id}?origin=${pkg.origin}&destination=${encodeURIComponent(pkg.destination)}&type=${pkg.type}&departure_date=${pkg.departure_date}&return_date=${pkg.return_date}&passengers=${pkg.passengers}`
    );
  };

  const howSteps = [
    { n: '01', title: t('home.how.step1Title'), body: t('home.how.step1Body') },
    { n: '02', title: t('home.how.step2Title'), body: t('home.how.step2Body') },
    { n: '03', title: t('home.how.step3Title'), body: t('home.how.step3Body') },
  ];

  const travellerTiers = [
    { name: t('home.pricing.planningName'), meta: t('home.pricing.planningMeta'), price: t('home.pricing.free'), free: true },
  ];

  const renderNewsletter = (stacked) => (
    <Box
      sx={{
        p: { xs: 2.5, md: 3 },
        bgcolor: HOME.surface,
        border: `1px solid ${HOME.ruleHard}`,
      }}
    >
      <Typography sx={{ ...documentEyebrowSx, mb: 1 }}>{t('home.community.newsletterTitle')}</Typography>
      <Typography sx={{ color: HOME.ink2, fontSize: 13.5, mb: 2, lineHeight: 1.65 }}>
        {t('home.community.newsletterBody')}
      </Typography>
      {newsletterSuccess ? (
        <Typography sx={{ color: HOME.accent, fontSize: 14, fontWeight: 500 }}>
          {t('home.community.subscribed')}
        </Typography>
      ) : (
        <>
          <Box
            component="form"
            onSubmit={handleNewsletterSubmit}
            sx={{ display: 'flex', flexDirection: stacked ? 'column' : { xs: 'row', md: 'column' }, gap: 1 }}
          >
            <input
              type="email"
              name="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder={t('home.community.emailPlaceholder')}
              required
              disabled={newsletterLoading}
              style={{
                flex: 1,
                width: '100%',
                padding: '12px 14px',
                border: `1px solid ${HOME.ruleHard}`,
                fontSize: 14,
                backgroundColor: HOME.canvas,
                outline: 'none',
                fontFamily: 'inherit',
                color: HOME.ink,
                boxSizing: 'border-box',
              }}
            />
            <Button type="submit" disabled={newsletterLoading} sx={{ ...partnerOxbloodBtnSx, ...fullWidthOnMobileSx }}>
              {newsletterLoading ? '...' : t('home.community.subscribe')}
            </Button>
          </Box>
          {newsletterError && (
            <Typography sx={{ color: '#8C2B22', fontSize: 12.5, mt: 1 }}>{newsletterError}</Typography>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box
      data-testid="home-page"
      sx={{
        bgcolor: HOME.canvas,
        color: HOME.ink,
        fontFamily: HOME.sans,
        overflowX: 'hidden',
        maxWidth: '100%',
      }}
    >
      <HealthStatusBanner />
      <SEOHead
        title="Vacation Planning Made Easy | Free Trip Planner App - Déjà Vu Nomad"
        description="Plan your perfect vacation with our free trip planner. Organize group travel, create itineraries, book flights & hotels, and collaborate with friends. Start planning your dream vacation today!"
        keywords="vacation planning, trip planner, vacation planner, travel planning app, group vacation planning, family vacation planner, trip itinerary, vacation organizer, travel itinerary maker, holiday planning, vacation booking, trip planning app free"
        canonical="https://dejavunomad.com/"
        structuredData={[...homepageSchemas, faqSchema]}
      />

      <CoverBand
        src={HERO_IMAGE}
        alt={t('home.hero.imageAlt')}
        aspectRatio={false}
        minHeight={{ xs: 'auto', md: 560 }}
        veilSx={{
          background: {
            xs: 'linear-gradient(to top, rgba(18,5,8,0.92) 0%, rgba(18,5,8,0.72) 48%, rgba(18,5,8,0.48) 100%)',
            md: 'linear-gradient(105deg, rgba(18,5,8,0.88) 0%, rgba(18,5,8,0.7) 34%, rgba(18,5,8,0.28) 62%, rgba(18,5,8,0.1) 100%)',
          },
        }}
      >
        <Box
          data-testid="home-page-wrap"
          sx={{
            ...WRAP_SX,
            pt: { xs: 4, sm: 5, md: 5.5 },
            pb: { xs: 3.5, md: 6.5 },
          }}
        >
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              mb: 2,
              overflow: 'visible',
            }}
          >
            <Box
              component="img"
              src={homeLogoSrc}
              alt={homeLogoAlt}
              sx={{
                width: '100%',
                maxWidth: { sm: 180, md: 220 },
                height: 'auto',
                display: 'block',
                objectFit: 'contain',
                objectPosition: 'left top',
                filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.55))',
              }}
            />
          </Box>
          <Typography
            sx={{
              ...documentEyebrowSx,
              color: HOME.brass,
              textShadow: '0 1px 8px rgba(0,0,0,0.55)',
            }}
          >
            {t('home.hero.eyebrow')}
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: HOME.display,
              fontSize: { xs: 34, sm: 48, md: 80 },
              lineHeight: 1.05,
              fontWeight: 400,
              color: '#FFF8EE',
              maxWidth: { xs: '12ch', md: '15ch' },
              mt: { xs: 1.25, md: 1.75 },
              overflowWrap: 'break-word',
              textShadow: '0 2px 18px rgba(0,0,0,0.55)',
            }}
          >
            {t('home.hero.title')}{' '}
            <Box component="em" sx={{ fontStyle: 'italic', color: '#E4C36A', textShadow: '0 2px 16px rgba(0,0,0,0.5)' }}>
              {t('home.hero.titleEmphasis')}
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 15, md: 17 },
              lineHeight: 1.65,
              color: '#F3E6D8',
              mt: { xs: 1.75, md: 2.25 },
              maxWidth: '44ch',
              textShadow: '0 1px 10px rgba(0,0,0,0.55)',
            }}
          >
            {t('home.hero.description')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.75} useFlexGap flexWrap="wrap" sx={{ mt: { xs: 2.5, md: 3.5 } }}>
            <Button onClick={() => navigate(cta.path)} sx={{ ...brassBtnSx, ...fullWidthOnMobileSx }}>
              {cta.label}
            </Button>
            <Button
              component={RouterLink}
              to={getPackagesListingPath()}
              sx={{ ...ghostBtnSx, ...fullWidthOnMobileSx, background: 'rgba(18,5,8,0.35)' }}
            >
              {t('home.hero.browsePackages')}
            </Button>
          </Stack>
          {shouldShowAppDownload && (
            <Box sx={{ mt: 2 }}>
              <AppStoreBadges />
            </Box>
          )}
          {!user && !isPartnerLoggedIn && (
            <Box sx={{ mt: 2 }}>
              <Typography component="span" sx={{ color: '#F3E6D8', fontSize: 13, mr: 1, textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
                {t('home.hero.travelAgent')}
              </Typography>
              <Button
                component={RouterLink}
                to="/partner/signup"
                sx={{
                  ...partnerLinkSx,
                  color: HOME.brass,
                  borderBottomColor: 'rgba(201,169,97,0.4)',
                  '&:hover': { color: '#F7F1E8', borderBottomColor: HOME.brass, background: 'none' },
                }}
              >
                {t('home.hero.joinPartner')}
              </Button>
            </Box>
          )}
        </Box>
      </CoverBand>

      {trustItems.length > 0 && (
        <Box sx={{ bgcolor: HOME.oxbloodDeep, color: '#DCC9BC' }}>
          <Box
            sx={{
              ...WRAP_SX,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${Math.min(trustItems.length, 4)}, 1fr)` },
            }}
          >
            {trustItems.map((item, index) => (
              <Box
                key={item.label}
                sx={{
                  py: { xs: 2, md: 2.5 },
                  px: { xs: 1, md: 1.5 },
                  textAlign: 'center',
                  minWidth: 0,
                  borderRight: {
                    xs: index % 2 === 0 ? '1px solid rgba(201,169,97,0.2)' : 0,
                    md: index < trustItems.length - 1 ? '1px solid rgba(201,169,97,0.2)' : 0,
                  },
                  borderBottom: {
                    xs: index < 2 && trustItems.length > 2 ? '1px solid rgba(201,169,97,0.2)' : 0,
                    md: 0,
                  },
                }}
              >
                <Typography sx={{ ...documentEyebrowSx, color: HOME.brass, mb: 0.75 }}>{item.label}</Typography>
                <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 22, md: 26 }, color: '#F7F1E8' }}>{item.value}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={SECTION_SX} data-testid="home-ai-planner">
        <Box sx={WRAP_SX}>
          <Box sx={{ maxWidth: '60ch' }}>
            <Typography sx={documentEyebrowSx}>{t('home.aiPlanner.eyebrow')}</Typography>
            <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 28, md: 46 }, fontWeight: 400, lineHeight: 1.05, mt: 1.5, mb: 1.75 }}>
              {t('home.aiPlanner.title')}
            </Typography>
            <Typography sx={{ fontSize: { xs: 15, md: 16.5 }, color: HOME.ink2, lineHeight: 1.72 }}>
              {t('home.aiPlanner.body')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{ ...SECTION_SX, bgcolor: HOME.surface, borderTop: `1px solid ${HOME.ruleHard}`, borderBottom: `1px solid ${HOME.ruleHard}` }}
        data-testid="home-map-section"
      >
        <Box sx={WRAP_SX}>
          <Box sx={{ maxWidth: '60ch' }}>
            <Typography sx={documentEyebrowSx}>{t('home.mapSection.eyebrow')}</Typography>
            <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 28, md: 46 }, fontWeight: 400, lineHeight: 1.05, mt: 1.5, mb: 1.75 }}>
              {t('home.mapSection.title')}
            </Typography>
            <Typography sx={{ fontSize: { xs: 15, md: 16.5 }, color: HOME.ink2, lineHeight: 1.72 }}>
              {t('home.mapSection.body')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={SECTION_SX}>
        <Box sx={WRAP_SX}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 2.5, md: 3.25 },
            }}
          >
            {[
              {
                image: DOOR_TRAVEL_IMAGE,
                eyebrow: t('home.doors.travellersEyebrow'),
                title: t('home.doors.travellersTitle'),
                body: t('home.doors.travellersBody'),
                items: [t('home.doors.travellers1'), t('home.doors.travellers2'), t('home.doors.travellers3')],
                cta: t('home.doors.travellersCta'),
                path: user ? '/trip-planner' : '/register',
              },
              {
                image: DOOR_PARTNER_IMAGE,
                eyebrow: t('home.doors.partnersEyebrow'),
                title: t('home.doors.partnersTitle'),
                body: t('home.doors.partnersBody'),
                items: [t('home.doors.partners1'), t('home.doors.partners2'), t('home.doors.partners3')],
                cta: t('home.doors.partnersCta'),
                path: '/partner',
              },
            ].map((door) => (
              <Box
                key={door.title}
                sx={{
                  bgcolor: HOME.surface,
                  border: `1px solid ${HOME.ruleHard}`,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <PlateImage src={door.image} alt={door.title} ratio="3 / 2" />
                <Box sx={{ p: { xs: 2.25, md: 3.5 }, flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography sx={documentEyebrowSx}>{door.eyebrow}</Typography>
                  <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 24, md: 29 }, fontWeight: 400, mt: 1.25, mb: 1.25, lineHeight: 1.08 }}>
                    {door.title}
                  </Typography>
                  <Typography sx={{ fontSize: 15, color: HOME.ink2, lineHeight: 1.7, mb: 2.5 }}>{door.body}</Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 3 }}>
                    {door.items.map((item) => (
                      <Box
                        component="li"
                        key={item}
                        sx={{
                          py: 1.1,
                          borderBottom: `1px solid ${HOME.rule}`,
                          fontSize: 14,
                          display: 'flex',
                          gap: 1.5,
                        }}
                      >
                        <Box component="span" sx={{ color: HOME.accent, fontSize: 11, pt: 0.5 }}>✓</Box>
                        <span>{item}</span>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ mt: 'auto' }}>
                    <Button onClick={() => navigate(door.path)} sx={{ ...partnerOxbloodBtnSx, ...fullWidthOnMobileSx }}>
                      {door.cta}
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {featuredDestinations.length > 0 && (
        <Box sx={{ ...SECTION_SX, bgcolor: HOME.surface, borderTop: `1px solid ${HOME.ruleHard}`, borderBottom: `1px solid ${HOME.ruleHard}` }}>
          <Box sx={WRAP_SX}>
            <Box sx={{ maxWidth: '60ch', mb: { xs: 3, md: 4.5 } }}>
              <Typography sx={documentEyebrowSx}>{t('home.destinations.eyebrow')}</Typography>
              <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 28, md: 46 }, fontWeight: 400, lineHeight: 1.05, mt: 1.5 }}>
                {t('home.destinations.title')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: { xs: 1.25, md: 1.75 },
              }}
            >
              {featuredDestinations.slice(0, 6).map((dest, index) => (
                <Box
                  key={dest.slug || dest.id || dest.name}
                  component={RouterLink}
                  to={`/destinations/${dest.slug}`}
                  sx={{
                    position: 'relative',
                    textDecoration: 'none',
                    color: '#F7F1E8',
                    minWidth: 0,
                    gridColumn: { xs: 'auto', md: mosaicSpans[index] === 2 ? 'span 2' : 'auto' },
                    '&:hover .home-veil': {
                      background: 'linear-gradient(to top, rgba(18,5,8,0.72) 0%, rgba(18,5,8,0.2) 50%, transparent 78%)',
                    },
                  }}
                >
                  <PlateImage
                    src={dest.image_url}
                    alt={dest.name}
                    ratio={mosaicRatios[index] || '3 / 2'}
                    photographerName={dest.unsplash_photographer_name}
                    photographerUsername={dest.unsplash_photographer_username}
                  >
                    <Box
                      className="home-veil"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to top, rgba(18,5,8,0.82) 0%, rgba(18,5,8,0.28) 44%, rgba(18,5,8,0.06) 72%)',
                        zIndex: 2,
                      }}
                    />
                    <Box sx={{ position: 'absolute', left: 16, right: 16, bottom: 14, zIndex: 3 }}>
                      <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 20, md: 23 }, fontWeight: 400, lineHeight: 1.1 }}>
                        {dest.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: '#DCC9BC', mt: 0.5 }}>
                        {dest.country}
                        {dest.guide_count
                          ? ` · ${dest.guide_count} ${Number(dest.guide_count) === 1 ? t('home.destinations.guide') : t('home.destinations.guides')}`
                          : ''}
                      </Typography>
                    </Box>
                  </PlateImage>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={SECTION_SX}>
        <Box sx={WRAP_SX}>
          <Box sx={{ maxWidth: '60ch', mb: { xs: 3, md: 4.5 } }}>
            <Typography sx={documentEyebrowSx}>{t('home.how.eyebrow')}</Typography>
            <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 28, md: 46 }, fontWeight: 400, lineHeight: 1.05, mt: 1.5 }}>
              {t('home.how.title')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 400px' },
              gap: { xs: 4, md: 6.5 },
              alignItems: 'start',
            }}
          >
            <Box sx={{ display: 'grid', gap: '1px', bgcolor: HOME.ruleHard, border: `1px solid ${HOME.ruleHard}` }}>
              {howSteps.map((step) => (
                <Box
                  key={step.n}
                  sx={{
                    bgcolor: HOME.surface,
                    p: { xs: 2.5, md: 3 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '52px 1fr' },
                    gap: { xs: 1, sm: 2.25 },
                  }}
                >
                  <Typography sx={{ fontFamily: HOME.display, fontSize: 30, color: HOME.accent, lineHeight: 1 }}>
                    {step.n}
                  </Typography>
                  <Box>
                    <Typography sx={{ fontFamily: HOME.display, fontSize: 20, fontWeight: 400, mb: 0.75 }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: HOME.ink2, lineHeight: 1.7 }}>{step.body}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ bgcolor: HOME.surface, border: `1px solid ${HOME.ruleHard}`, px: 2.75, pt: 2.5, pb: 1.75 }}>
              <Typography sx={{ ...documentEyebrowSx, mb: 1.75 }}>{t('home.how.shotLabel')}</Typography>
              <Typography sx={{ fontFamily: HOME.display, fontSize: 22, fontWeight: 400, mb: 0.25 }}>
                {t('home.how.shotTitle')}
              </Typography>
              <Typography sx={{ fontSize: 12, color: HOME.ink3, mb: 2 }}>{t('home.how.shotMeta')}</Typography>
              {[
                { time: '11:00', title: t('home.how.shot1Title'), meta: t('home.how.shot1Meta'), gap: t('home.how.shotGap1') },
                { time: '13:30', title: t('home.how.shot2Title'), meta: t('home.how.shot2Meta'), gap: t('home.how.shotGap2') },
                { time: '19:30', title: t('home.how.shot3Title'), meta: t('home.how.shot3Meta') },
              ].map((row) => (
                <Box key={row.time}>
                  <Box sx={{ position: 'relative', pl: { xs: 6.5, sm: 8.75 }, py: 1.5, borderTop: `1px solid ${HOME.rule}` }}>
                    <Typography sx={{ position: 'absolute', left: 0, top: 16, width: { xs: 40, sm: 44 }, textAlign: 'right', fontSize: { xs: 11.5, sm: 12.5 }, color: HOME.ink2 }}>
                      {row.time}
                    </Typography>
                    <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 15, md: 16 }, fontWeight: 400 }}>{row.title}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: HOME.ink3, mt: 0.25 }}>{row.meta}</Typography>
                  </Box>
                  {row.gap && (
                    <Typography sx={{ fontSize: 11, color: HOME.accent, py: 0.75, pl: { xs: 6.5, sm: 8.75 } }}>{row.gap}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ ...SECTION_SX, bgcolor: HOME.surface, borderTop: `1px solid ${HOME.ruleHard}`, borderBottom: `1px solid ${HOME.ruleHard}` }}>
        <Box sx={WRAP_SX}>
          <Box sx={{ maxWidth: '60ch', mb: { xs: 3, md: 4.5 } }}>
            <Typography sx={documentEyebrowSx}>{t('home.packages.eyebrow')}</Typography>
            <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 28, md: 46 }, fontWeight: 400, lineHeight: 1.05, mt: 1.5, mb: 1.75 }}>
              {t('home.packages.title')}
            </Typography>
            <Typography sx={{ fontSize: { xs: 15, md: 16.5 }, color: HOME.ink2, lineHeight: 1.72 }}>
              {t('home.packages.description')}
            </Typography>
          </Box>
          {packagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={40} sx={{ color: HOME.oxblood }} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: 2.5,
              }}
            >
              {displayPackages.map((pkg) => {
                const isPartner = usingPartnerPackages || Boolean(pkg.slug && pkg.base_price != null);
                const imageUrl = pkg.cover_image || pkg.imageUrl || FALLBACK_IMAGE;
                const title = pkg.title || pkg.destination;
                const price = formatMoney(isPartner ? pkg.base_price : pkg.pricing?.total);
                const duration = isPartner
                  ? t('home.packages.daysNights', { days: pkg.duration_days, nights: pkg.duration_nights })
                  : t('home.packages.durationDays', { count: pkg.duration });
                const typeLabel = pkg.package_type || pkg.type;
                const operator = pkg.partner_name
                  ? t('home.packages.operatorVerified', { name: pkg.partner_name })
                  : null;

                return (
                  <Box
                    key={pkg.id || pkg.slug || title}
                    sx={{
                      bgcolor: HOME.surface,
                      border: `1px solid ${HOME.ruleHard}`,
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                    }}
                  >
                    <PlateImage
                      src={imageUrl}
                      alt={title}
                      ratio="4 / 3"
                      photographerName={pkg.unsplash_photographer_name}
                      photographerUsername={pkg.unsplash_photographer_username}
                    />
                    <Box sx={{ px: 2.25, pt: 2, pb: 1, flex: 1 }}>
                      {typeLabel && (
                        <Typography sx={{ ...documentEyebrowSx, textTransform: 'uppercase' }}>{typeLabel}</Typography>
                      )}
                      <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 20, md: 21 }, fontWeight: 400, lineHeight: 1.14, mt: 0.75, overflowWrap: 'break-word' }}>
                        {title}
                      </Typography>
                      {duration && (
                        <Typography sx={{ fontSize: 12.5, color: HOME.ink3, mt: 0.5 }}>{duration}</Typography>
                      )}
                      {operator && (
                        <Typography sx={{ fontSize: 12, color: HOME.ink2, mt: 1.25 }}>{operator}</Typography>
                      )}
                      {price && (
                        <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 20, md: 23 }, mt: 1.5, overflowWrap: 'break-word' }}>
                          {t('home.packages.fromPrice', { price })}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      onClick={() => (isPartner && pkg.slug ? navigate(`/packages/${pkg.slug}`) : openGeneratedPackage(pkg))}
                      sx={{
                        border: 0,
                        borderTop: `1px solid ${HOME.rule}`,
                        borderRadius: 0,
                        py: 1.5,
                        px: 2.25,
                        fontSize: 10.5,
                        letterSpacing: '0.13em',
                        textTransform: 'uppercase',
                        color: HOME.ink2,
                        justifyContent: 'flex-start',
                        boxShadow: 'none',
                        '&:hover': { color: HOME.ink, bgcolor: '#F6F1E7', boxShadow: 'none' },
                      }}
                    >
                      {t('home.packages.viewPackage')}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
          <Box sx={{ mt: 3 }}>
            <Button component={RouterLink} to={getPackagesListingPath()} sx={partnerLinkSx}>
              {t('home.packages.viewAll')}
            </Button>
          </Box>
        </Box>
      </Box>

      <CoverBand
        src={BAND_IMAGE}
        alt={t('home.quote.imageAlt')}
        alignItems="center"
        minHeight={{ xs: 'auto', md: 400 }}
        veilSx={{ background: 'linear-gradient(90deg, rgba(18,5,8,0.9) 0%, rgba(18,5,8,0.62) 46%, rgba(18,5,8,0.25) 100%)' }}
      >
        <Box sx={{ ...WRAP_SX, py: { xs: 6, md: 0 } }}>
          <Typography
            component="blockquote"
            sx={{
              fontFamily: HOME.display,
              fontSize: { xs: 24, sm: 32, md: 42 },
              lineHeight: 1.22,
              color: '#F7F1E8',
              maxWidth: { xs: '18ch', md: '22ch' },
              m: 0,
            }}
          >
            {t('home.quote.text')}
          </Typography>
          <Typography sx={{ fontSize: { xs: 13, md: 13.5 }, color: '#DCC9BC', mt: { xs: 2, md: 2.75 } }}>
            {t('home.quote.name')}
            <Box component="span" sx={{ display: 'block', fontSize: 12, color: HOME.brass, mt: 0.5, letterSpacing: '0.09em' }}>
              {t('home.quote.role')}
            </Box>
          </Typography>
        </Box>
      </CoverBand>

      {communityGuides.length > 0 && (
        <Box sx={SECTION_SX}>
          <Box sx={WRAP_SX}>
            <Box sx={{ maxWidth: '60ch', mb: { xs: 3, md: 4.5 } }}>
              <Typography sx={documentEyebrowSx}>{t('home.guides.eyebrow')}</Typography>
              <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 28, md: 46 }, fontWeight: 400, lineHeight: 1.05, mt: 1.5, mb: 1.75 }}>
                {t('home.guides.title')}
              </Typography>
              <Typography sx={{ fontSize: { xs: 15, md: 16.5 }, color: HOME.ink2, lineHeight: 1.72 }}>
                {t('home.guides.subtitle')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2.5,
              }}
            >
              {communityGuides.map((guide) => (
                <Box
                  key={guide.id || guide.slug}
                  component={RouterLink}
                  to={`/guides/${guide.slug}`}
                  sx={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <PlateImage
                    src={guide.cover_image}
                    alt={guide.title}
                    ratio="3 / 2"
                    photographerName={guide.unsplash_photographer_name}
                    photographerUsername={guide.unsplash_photographer_username}
                  />
                  <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 20, md: 22 }, fontWeight: 400, mt: 1.5, mb: 0.75, lineHeight: 1.15, overflowWrap: 'break-word' }}>
                    {guide.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: HOME.ink2, lineHeight: 1.65 }}>
                    {guide.description || guide.destination}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: HOME.ink3, mt: 1 }}>
                    {t('home.guides.byReads', {
                      name: guide.author?.name || t('home.guides.traveler'),
                      reads: guide.view_count > 999 ? `${(guide.view_count / 1000).toFixed(1)}k` : (guide.view_count || 0),
                    })}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ ...SECTION_SX, bgcolor: HOME.oxbloodDeep }}>
        <Box sx={WRAP_SX}>
          <Box
            onClick={() => navigate('/forum')}
            sx={{ textAlign: 'center', mb: { xs: 3, md: 4 }, cursor: 'pointer', '&:hover': { opacity: 0.88 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
              <ForumIcon sx={{ color: HOME.brass, fontSize: '1.5rem' }} />
              <Typography sx={{ ...documentEyebrowSx, color: HOME.brass }}>{t('home.community.live')}</Typography>
            </Box>
            <Typography sx={{ fontFamily: HOME.display, color: '#F7F1E8', fontSize: { xs: 28, md: 42 }, mb: 0.75 }}>
              {t('home.community.title')}
            </Typography>
            <Typography sx={{ color: 'rgba(246,239,228,0.62)', fontSize: 15, maxWidth: 500, mx: 'auto' }}>
              {forumStats.totalPosts > 0
                ? t('home.community.subtitleCount', { count: forumStats.totalPosts.toLocaleString() })
                : t('home.community.subtitle')}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
              gap: 3,
            }}
          >
            <Box sx={{ bgcolor: HOME.surface, overflow: 'hidden', border: `1px solid ${HOME.ruleHard}` }}>
              <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderBottom: `1px solid ${HOME.rule}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingIcon sx={{ color: HOME.oxblood, fontSize: '1.1rem' }} />
                <Typography sx={{ color: HOME.ink, fontWeight: 500, fontSize: 14 }}>{t('home.community.trending')}</Typography>
              </Box>
              {forumLoading ? (
                [...Array(4)].map((_, i) => (
                  <Box key={i} sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: `1px solid ${HOME.rule}` }}>
                    <Skeleton width="80%" height={20} />
                    <Skeleton width="40%" height={16} sx={{ mt: 0.5 }} />
                  </Box>
                ))
              ) : trendingPosts.length > 0 ? (
                trendingPosts.map((post, i) => (
                  <Box
                    key={post.id}
                    onClick={() => navigate(`/forum/${post.slug}`)}
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 1.25, md: 2 },
                      minWidth: 0,
                      cursor: 'pointer',
                      borderBottom: i < trendingPosts.length - 1 ? `1px solid ${HOME.rule}` : 'none',
                      '&:hover': { bgcolor: '#F6F1E7' },
                    }}
                  >
                    <Avatar src={post.author_avatar} alt={post.author_name} sx={{ width: { xs: 32, md: 36 }, height: { xs: 32, md: 36 }, bgcolor: HOME.oxblood, fontSize: 14, flexShrink: 0 }}>
                      {post.author_name?.charAt(0)?.toUpperCase() || '?'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: HOME.ink, fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {post.flag_emoji ? `${post.flag_emoji} ` : ''}{post.title}
                      </Typography>
                      <Typography sx={{ color: HOME.ink3, fontSize: 12 }}>
                        {post.author_name || t('home.guides.traveler')} · {timeAgo(post.created_at)}
                        {post.category_name ? ` · ${post.category_name}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CommentIcon sx={{ fontSize: 14, color: HOME.oxblood }} />
                        <Typography sx={{ fontSize: 13, color: HOME.ink2 }}>{post.comment_count || 0}</Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                        <ViewIcon sx={{ fontSize: 14, color: HOME.ink3 }} />
                        <Typography sx={{ fontSize: 13, color: HOME.ink3 }}>
                          {post.view_count > 999 ? `${(post.view_count / 1000).toFixed(1)}k` : post.view_count || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box sx={{ px: { xs: 2, md: 3 }, py: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: HOME.ink3, fontSize: 14 }}>{t('home.community.empty')}</Typography>
                </Box>
              )}
              <Box
                onClick={() => navigate('/forum')}
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1.5,
                  borderTop: `1px solid ${HOME.rule}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#F6F1E7' },
                }}
              >
                <Typography sx={{ color: HOME.oxblood, fontSize: 13.5, fontWeight: 600 }}>{t('home.community.viewAll')}</Typography>
                <ArrowForwardIcon sx={{ color: HOME.oxblood, fontSize: 16 }} />
              </Box>
            </Box>

            <Stack spacing={2.5}>
              {renderNewsletter(true)}
              <Box sx={{ p: 3, bgcolor: HOME.surface, border: `1px solid ${HOME.ruleHard}`, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: HOME.display, fontSize: 22, mb: 1 }}>{t('home.community.shareTitle')}</Typography>
                <Typography sx={{ color: HOME.ink2, fontSize: 13.5, mb: 2.5, lineHeight: 1.6 }}>
                  {t('home.community.shareBody')}
                </Typography>
                <Stack spacing={1.25}>
                  <Button startIcon={<WriteIcon />} onClick={() => navigate(user ? '/forum/new' : '/register')} sx={{ ...partnerOxbloodBtnSx, ...fullWidthOnMobileSx }}>
                    {t('home.community.startDiscussion')}
                  </Button>
                  <Button startIcon={<ForumIcon />} onClick={() => navigate('/forum')} sx={{ ...partnerLinkSx, alignSelf: 'center' }}>
                    {t('home.community.explore')}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      {!isMobileApp() && (
        <Box sx={{ ...SECTION_SX, bgcolor: HOME.surface, borderTop: `1px solid ${HOME.ruleHard}` }}>
          <Box sx={WRAP_SX}>
            <Box sx={{ maxWidth: '60ch', mb: { xs: 3, md: 4.5 } }}>
              <Typography sx={documentEyebrowSx}>{t('home.pricing.eyebrow')}</Typography>
              <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 28, md: 46 }, fontWeight: 400, lineHeight: 1.05, mt: 1.5, mb: 1.75 }}>
                {t('home.pricing.title')}
              </Typography>
              <Typography sx={{ fontSize: { xs: 15, md: 16.5 }, color: HOME.ink2, lineHeight: 1.72 }}>
                {t('home.pricing.subtitle')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1.55fr 1fr' },
                border: `1px solid ${HOME.ruleHard}`,
                bgcolor: HOME.surface,
              }}
            >
              <Box sx={{ p: { xs: 2.25, md: 4 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography sx={documentEyebrowSx}>{t('home.pricing.travellersEyebrow')}</Typography>
                <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 24, md: 32 }, fontWeight: 400, lineHeight: 1.08, my: 1.25 }}>
                  {t('home.pricing.travellersTitle')}
                </Typography>
                <Typography sx={{ fontSize: 15, color: HOME.ink2, lineHeight: 1.7, maxWidth: '46ch', mb: 2.75 }}>
                  {t('home.pricing.travellersClaim')}
                </Typography>
                <Box sx={{ borderTop: `1px solid ${HOME.rule}` }}>
                  {travellerTiers.map((tier) => (
                    <Box
                      key={tier.name}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 1,
                        alignItems: 'baseline',
                        py: 1.6,
                        borderBottom: `1px solid ${HOME.rule}`,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 14.5 }}>{tier.name}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: HOME.ink3, mt: 0.5 }}>{tier.meta}</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 18, md: 22 }, color: tier.free ? HOME.accent : HOME.ink, whiteSpace: 'nowrap' }}>
                        {tier.price}
                        {tier.suffix && (
                          <Box component="span" sx={{ fontFamily: HOME.sans, fontSize: 11.5, color: HOME.ink3, ml: 0.5 }}>
                            {tier.suffix}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mt: 'auto', pt: 3.25 }}>
                  <Button
                    onClick={() => navigate(user ? '/trips' : '/register')}
                    sx={{ ...partnerOxbloodBtnSx, ...fullWidthOnMobileSx }}
                  >
                    {t('home.pricing.startTrip')}
                  </Button>
                </Box>
              </Box>

              {/* The trade proposition is a quiet aside rather than a matching
                  panel: /partner is where agents are actually sold to, and an
                  equal-weight column here implied the two audiences arrive in
                  equal numbers. */}
              <Box
                sx={{
                  p: { xs: 2.25, md: 4 },
                  bgcolor: '#F4EFE4',
                  borderTop: { xs: `1px solid ${HOME.ruleHard}`, md: 'none' },
                  borderLeft: { md: `1px solid ${HOME.ruleHard}` },
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                }}
              >
                <Typography sx={documentEyebrowSx}>{t('home.pricing.partnersEyebrow')}</Typography>
                <Typography sx={{ fontFamily: HOME.display, fontSize: { xs: 20, md: 24 }, fontWeight: 400, lineHeight: 1.12, my: 1.25 }}>
                  {t('home.pricing.partnersTitle')}
                </Typography>
                <Typography sx={{ fontSize: 14, color: HOME.ink2, lineHeight: 1.7, mb: 2.5 }}>
                  {t('home.pricing.partnersClaim')}
                </Typography>
                <Box sx={{ borderTop: `1px solid ${HOME.rule}` }}>
                  {[
                    { label: t('home.pricing.ownClients'), value: '0%', accent: true },
                    { label: t('home.pricing.startsAt'), value: t('home.pricing.freeOneSeat') },
                  ].map((row) => (
                    <Box key={row.label} sx={{ py: 1.5, borderBottom: `1px solid ${HOME.rule}` }}>
                      <Typography sx={{ fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: HOME.ink3, mb: 0.75 }}>
                        {row.label}
                      </Typography>
                      <Typography sx={{ fontSize: 17, color: row.accent ? HOME.accent : HOME.ink }}>{row.value}</Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  component={RouterLink}
                  to="/pricing"
                  sx={{ ...partnerLinkSx, alignSelf: 'flex-start', mt: 2.5 }}
                >
                  {t('home.pricing.seePartner')}
                </Button>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 13, color: HOME.ink3, mt: 3, lineHeight: 1.75, maxWidth: '78ch' }}>
              {t('home.pricing.footnote')}
            </Typography>
          </Box>
        </Box>
      )}

      <Box component="footer" sx={{ bgcolor: HOME.oxbloodDeep, color: '#DCC9BC', pt: { xs: 5, md: 7 }, pb: 4 }}>
        <Box sx={WRAP_SX}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.6fr 1fr 1fr 1fr' },
              gap: { xs: 3.5, md: 5 },
            }}
          >
            <Box sx={{ gridColumn: { xs: 'auto', sm: 'span 2', md: 'auto' } }}>
              <Box
                component="img"
                src={homeLogoSrc}
                alt={homeLogoAlt}
                sx={{ width: '100%', maxWidth: 180, height: 'auto', mb: partnerLogoUrl ? 1 : 1.75 }}
              />
              {partnerLogoUrl && (
                <Typography sx={{ fontSize: 11.5, letterSpacing: '0.06em', color: '#9C8176', mb: 1.75 }}>
                  {t('trip.poweredBy')}
                </Typography>
              )}
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.7, maxWidth: '38ch' }}>
                {t('home.footer.about')}
              </Typography>
              {shouldShowAppDownload && (
                <Box sx={{ mt: 2.25 }}>
                  <AppStoreBadges />
                </Box>
              )}
            </Box>
            <Box>
              <Typography sx={{ ...documentEyebrowSx, color: HOME.brass, mb: 1.75 }}>{t('home.footer.plan')}</Typography>
              <FooterNavLink to="/register">{t('home.footer.startPlanning')}</FooterNavLink>
              <FooterNavLink to="/trip-planner">{t('home.footer.tripPlanner')}</FooterNavLink>
              <FooterNavLink to="/group-vacation">{t('home.footer.groupTrips')}</FooterNavLink>
              <FooterNavLink to="/deals?tab=road-trips">{t('home.footer.roadTrips')}</FooterNavLink>
              <FooterNavLink to="/guides">{t('home.footer.travelGuides')}</FooterNavLink>
              <FooterNavLink to="/destinations">{t('home.footer.destinations')}</FooterNavLink>
              <FooterNavLink to={getPackagesListingPath()}>{t('home.footer.travelDeals')}</FooterNavLink>
              <FooterNavLink to="/pricing">{t('home.pricing.eyebrow')}</FooterNavLink>
            </Box>
            <Box>
              <Typography sx={{ ...documentEyebrowSx, color: HOME.brass, mb: 1.75 }}>{t('home.footer.partners')}</Typography>
              <FooterNavLink to="/partner">{t('home.footer.partnerProgramme')}</FooterNavLink>
              <FooterNavLink to="/partner/signup">{t('home.footer.publishPackage')}</FooterNavLink>
              <FooterNavLink to="/partner">{t('home.footer.partnerGuide')}</FooterNavLink>
              <FooterNavLink to="/forum">{t('home.footer.community')}</FooterNavLink>
            </Box>
            <Box>
              <Typography sx={{ ...documentEyebrowSx, color: HOME.brass, mb: 1.75 }}>{t('home.footer.legal')}</Typography>
              <FooterNavLink to="/privacy">{t('home.footer.privacy')}</FooterNavLink>
              <FooterNavLink to="/terms">{t('home.footer.terms')}</FooterNavLink>
              {isPartnerLoggedIn && (
                <FooterNavLink to="/partner-terms">{t('home.footer.partnerAgreement')}</FooterNavLink>
              )}
              <FooterNavLink to="/support">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SupportIcon sx={{ fontSize: 16 }} />
                  {t('home.footer.support')}
                </Box>
              </FooterNavLink>
              <Box component="a" href="mailto:support@dejavunomad.com" sx={footerLinkSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 16 }} />
                  {t('home.footer.contact')}
                </Box>
              </Box>
              <FooterNavLink to="/support">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HelpIcon sx={{ fontSize: 16 }} />
                  {t('home.footer.troubleshooting')}
                </Box>
              </FooterNavLink>
            </Box>
          </Box>

          <Box sx={{ mt: 5 }}>
            <Typography sx={{ fontSize: 11.5, lineHeight: 1.6, color: '#9C8176' }}>
              {t('sellerDisclosures.footerSellerLine')}
            </Typography>
            <Typography sx={{ fontSize: 11.5, lineHeight: 1.6, color: '#9C8176' }}>
              {t('sellerDisclosures.footerNotApproved')}
            </Typography>
          </Box>

          <Box sx={{ mt: 5, p: 3, border: '1px solid rgba(201,169,97,0.22)' }}>
            <Typography sx={{ mb: 2, fontWeight: 600, color: '#F7F1E8', fontFamily: HOME.display, fontSize: 20 }}>
              {t('home.footer.tipsTitle')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              {[
                { title: t('home.footer.tipLoginTitle'), body: t('home.footer.tipLoginBody') },
                { title: t('home.footer.tipLoadTitle'), body: t('home.footer.tipLoadBody') },
                { title: t('home.footer.tipInviteTitle'), body: t('home.footer.tipInviteBody') },
                { title: t('home.footer.tipHelpTitle'), body: t('home.footer.tipHelpBody') },
              ].map((tip) => (
                <Box key={tip.title}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 1, fontWeight: 600, fontSize: 14 }}>{tip.title}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>{tip.body}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2.75,
              flexWrap: 'wrap',
              mt: 5.5,
              pt: 2.5,
              borderTop: '1px solid rgba(201,169,97,0.22)',
              fontSize: 12,
            }}
          >
            <FooterNavLink to="/terms" sx={{ color: '#DCC9BC', textDecoration: 'underline' }}>
              {t('home.footer.terms')}
            </FooterNavLink>
            {isPartnerLoggedIn && (
              <FooterNavLink to="/partner-terms" sx={{ color: '#DCC9BC', textDecoration: 'underline' }}>
                {t('home.footer.partnerAgreement')}
              </FooterNavLink>
            )}
            <FooterNavLink to="/privacy" sx={{ color: '#DCC9BC', textDecoration: 'underline' }}>
              {t('home.footer.privacy')}
            </FooterNavLink>
            <Typography sx={{ ml: { xs: 0, md: 'auto' }, color: '#9C8176', fontSize: 12 }}>
              {t('home.footer.copyright', { year: new Date().getFullYear() })}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
